/**
 * Canonical permit field helpers — maps CRA forms ↔ GovOps program JSON.
 * API top-level: permitType, city, program, status, caseId, orgId, fee/payment fields.
 * Extended fields live in program (JSON string).
 */
import { pickFeeFields } from './feeFields';

export function formatPermitAddress(loc = {}) {
  if (!loc || typeof loc === 'string') return loc || '';
  const line1 = [loc.street, loc.unit].filter(Boolean).join(' ').trim();
  return [line1, loc.city, loc.state, loc.zipCode].filter(Boolean).join(', ');
}

/** Normalize alarm location from form or legacy permit row. */
export function normalizeAlarmLocation(source = {}) {
  const loc = source.alarmLocation || {};
  return {
    street: loc.street ?? source.street ?? '',
    unit: loc.unit ?? source.unit ?? '',
    city: loc.city ?? source.city ?? '',
    state: loc.state ?? source.state ?? '',
    zipCode: loc.zipCode ?? source.zipCode ?? '',
  };
}

/** Build legacy create payload (→ mapLegacyPermitToGovOps). */
export function buildCreatePermitPayload(form) {
  const alarmLocation = normalizeAlarmLocation(form);
  const coordinates = form.coordinates || null;
  const address = form.address || formatPermitAddress(alarmLocation);

  const payload = {
    type: form.type || form.permitType || form.propertyType || 'general',
    description: form.description || form.applicantName || 'Fire alarm permit',
    status: form.status || 'intake',
    address,
    alarmLocation: {
      ...alarmLocation,
      ...(coordinates ? { coordinates } : {}),
    },
    coordinates,
    applicantName: form.applicantName,
    phoneNumber: form.phoneNumber,
    contact: form.contact,
    billingAddress: form.billingAddress,
    alarmCompany: form.alarmCompany,
    propertyType: form.propertyType,
    checkNumber: form.checkNumber,
    permitNumber: form.permitNumber,
    date: form.date,
    caseId: form.caseId,
    intakeSource: form.intakeSource,
    intakeAt: form.intakeAt,
  };

  Object.keys(payload).forEach((k) => {
    if (payload[k] === undefined || payload[k] === null || payload[k] === '') {
      if (k !== 'type' && k !== 'description' && k !== 'status') delete payload[k];
    }
  });

  return { ...payload, ...pickFeeFields(form) };
}

/** Build legacy PATCH payload preserving all stored program fields. */
export function buildUpdatePermitPayload(permit) {
  if (!permit) return {};
  const alarmLocation = normalizeAlarmLocation(permit);
  const coords = permit.coordinates;
  const address =
    permit.address || formatPermitAddress(alarmLocation) || undefined;

  return {
    type: permit.type || permit.permitType,
    description: permit.description,
    status: permit.status,
    address,
    coordinates: coords || undefined,
    alarmLocation: {
      ...alarmLocation,
      ...(address ? { address } : {}),
      ...(coords ? { coordinates: coords } : {}),
    },
    applicantName: permit.applicantName,
    phoneNumber: permit.phoneNumber,
    contact: permit.contact,
    billingAddress: permit.billingAddress,
    alarmCompany: permit.alarmCompany,
    propertyType: permit.propertyType,
    checkNumber: permit.checkNumber,
    permitNumber: permit.permitNumber,
    date: permit.date,
    caseId: permit.caseId,
    ...pickFeeFields(permit),
  };
}

/** Human-readable rows for permit detail summary. */
export function getPermitDisplayFields(permit) {
  if (!permit) return [];
  const loc = normalizeAlarmLocation(permit);
  const contact = permit.contact || {};
  const rows = [
    ['Applicant', permit.applicantName],
    ['Phone', permit.phoneNumber],
    ['Property type', permit.propertyType || permit.type],
    ['Permit #', permit.permitNumber],
    ['Address', permit.address || formatPermitAddress(loc)],
    ['Street', loc.street],
    ['City', loc.city],
    ['State', loc.state],
    ['Zip', loc.zipCode],
    ['Contact', contact.name],
    ['Contact email', contact.email],
    ['Contact phone', contact.alternateNumber || contact.phone],
    ['Alarm company', permit.alarmCompany?.name],
    ['Company phone', permit.alarmCompany?.phone],
    ['Check #', permit.checkNumber],
    ['Fee amount', permit.feeAmount != null ? `${permit.feeAmount} ${permit.feeCurrency || 'USD'}` : null],
    ['Balance due', permit.balanceDue],
    ['Payment status', permit.paymentStatus],
    ['Renewal date', permit.renewalDate],
    ['Expiration', permit.expirationDate],
  ];
  return rows.filter(([, v]) => v != null && String(v).trim() !== '');
}
